/**
 * React 18 StrictMode-safe Video.js bootstrap hook.
 *
 * Creates a `<video-js>` element inside a container ref on mount, calls
 * `videojs(el, options, onReady)`, disposes the player on unmount, guards
 * against double-init, and re-initialises only when the memoised options
 * identity changes. Never renders a `<video>` element directly in JSX —
 * Video.js owns the media element lifecycle.
 */
import videojs from 'video.js';
import 'videojs-contrib-quality-levels';
import { useEffect, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';

/**
 * The Video.js player instance type.
 *
 * WHY: video.js 8's bundled types do not expose `videojs.Player` as a
 * namespace member, so derive it from the `videojs()` factory's return type
 * instead of reaching for a (non-existent) `videojs.Player` reference.
 */
export type Player = ReturnType<typeof videojs>;

/**
 * Player options used by Syncreel.
 *
 * WHY: video.js 8's bundled types declare the `videojs()` options parameter
 * as `any` and do not export a `PlayerOptions` interface, so we declare the
 * narrow surface we actually use here. This keeps the hook strictly typed
 * without redefining any frozen shared contract.
 */
export interface PlayerOptions {
  sources?: { src: string; type: string }[];
  /** VHS / HTML5 tech options. `overrideNative` forces VHS over native HLS. */
  html5?: { vhs?: { overrideNative?: boolean } };
  playbackRates?: number[];
  preload?: string;
  /** Keep playback inline on iOS; without it iPhone Safari opens the native fullscreen player on play, which hides the room UI. */
  playsinline?: boolean;
  fluid?: boolean;
  /** Whether to show the control bar (defaults to true in video.js). */
  controls?: boolean;
  /** Applied to the underlying `<video>` element (CORS for WS-7's audio graph). */
  crossOrigin?: string;
  userActions?: { hotkeys?: boolean };
  controlBar?: { children?: string[] };
}

/** Arguments to {@link useVideoJs}. */
export interface UseVideoJsArgs {
  /** Player options. Must be memoised by the caller; identity changes trigger re-init. */
  options: PlayerOptions;
  /** Called once when the player is ready (after sources are applied). */
  onReady?: (player: Player) => void;
}

/** Return value of {@link useVideoJs}. */
export interface UseVideoJsResult {
  /** Attach to the `<div>` that will host the Video.js element. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** The live player instance, or `null` before init / after dispose. */
  playerRef: MutableRefObject<Player | null>;
}

/**
 * Detect platforms where MSE for video is unavailable and Video.js falls
 * back to native HLS (notably iOS Safari). On these platforms VHS cannot
 * override native playback, so quality/audio menus are native and sync
 * menus degrade. The container is tagged with `data-native-hls` so WS-5/WS-7
 * can render an informational banner.
 *
 * WHY: iOS Safari lacks MSE for video — we force `overrideNative: true` in
 * options but it is ignored; we must degrade gracefully (sync still works,
 * some menus are native) rather than breaking.
 * @see master build prompt §9 — Safari native HLS diverges.
 */
function isNativeHlsOnly(): boolean {
  if (typeof navigator === 'undefined') return false;
  // WHY: feature-detect, don't sniff the UA. iPhone Safari exposes only
  // ManagedMediaSource (not MediaSource), which VHS does not use unless
  // `experimentalUseMMS` is set, so it falls back to native HLS. iPadOS and
  // macOS Safari have MediaSource and run VHS, so they keep the full menus.
  return typeof MediaSource === 'undefined';
}

/** Minimal shape of the contrib-quality-levels plugin. */
interface QualityPluginPlayer extends Player {
  qualityLevels(): unknown;
}

/**
 * Initialise quality plugins immediately after player creation, before VHS
 * starts parsing the HLS manifest. @see useVideoJs for why this must happen
 * before `onReady`.
 */
function setupQualityEarly(player: Player): void {
  const p = player as unknown as QualityPluginPlayer;
  try {
    p.qualityLevels();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Quality menu plugin failed to initialize (non-fatal):', err);
  }
}

/**
 * Bootstrap a Video.js player inside a container div.
 *
 * StrictMode safety: effects run mount → unmount → mount in development.
 * The cleanup disposes the player and nulls the ref, so the second mount
 * creates a fresh player. The `playerRef.current` guard additionally
 * protects against a true double-mount without an intervening cleanup.
 *
 * Re-init: the effect depends on `[options]` only. The caller must memoise
 * `options` (e.g. `useMemo` over `[media, role]`); a new identity disposes
 * the old player and creates a new one. `onReady` is kept in a ref so a
 * changing callback identity never triggers a re-init.
 */
export function useVideoJs(args: UseVideoJsArgs): UseVideoJsResult {
  const { options } = args;
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  // WHY: keep the latest onReady in a ref so the effect can depend on
  // `options` alone without re-initialising when the callback identity
  // changes (the parent re-renders with a new closure each render).
  const onReadyRef = useRef<UseVideoJsArgs['onReady']>(args.onReady);
  onReadyRef.current = args.onReady;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // Guard double-init: if a player somehow already exists, do not create
    // a second one over the same element.
    if (playerRef.current) return;

    const el = document.createElement('video-js');
    el.className = 'video-js vjs-default-skin vjs-big-play-centered';
    if (isNativeHlsOnly()) {
      el.setAttribute('data-native-hls', '');
    }
    container.appendChild(el);

    const player = videojs(el, options, () => {
      // WHY: in React 18 StrictMode the first player can be disposed before
      // its async ready callback fires (mount → unmount → mount). Guard so we
      // never hand a disposed instance to onReady; the second mount's player
      // will fire its own ready callback.
      if (player.isDisposed()) return;
      onReadyRef.current?.(player);
    });
    playerRef.current = player;

    // WHY: quality plugins must be initialised BEFORE VHS starts parsing the
    // HLS manifest. VHS fires `addqualitylevel` events on the quality levels
    // list as it parses the master playlist, and the settings panel
    // listens for those events to build its menu. If we wait until `onReady`,
    // the events have already fired and the menu is empty. Calling here —
    // right after `videojs()` returns but before the ready callback — ensures
    // the listeners are registered in time.
    setupQualityEarly(player);

    return () => {
      const p = playerRef.current;
      if (p && !p.isDisposed()) {
        p.dispose();
      }
      // WHY: dispose() removes the <video-js> element from the DOM, but we
      // null the ref explicitly so the guard and `player()` handle report
      // null cleanly between an unmount and the next mount.
      playerRef.current = null;
    };
  }, [options]);

  return { containerRef, playerRef };
}
